import _ from "lodash";
import { make } from "vuex-pathify";
import ProgramEnum from "@/util/programEnum";

const initialState = {
    authorizations: _.get(window, "_CS_AUTHORIZATIONS", []),
    dealerPermissions: _.get(window, "_CS_DEALER_PERMISSIONS", {}),
    firstName: _.get(window, "_CS_CONTEXT.firstName", null),
    lastName: _.get(window, "_CS_CONTEXT.lastName", null),
    email: _.get(window, "_CS_CONTEXT.email", null),
    userId: _.get(window, "_CS_CONTEXT.userId", null),
    userDealerAccessList: _.get(window, "_CS_USER_DEALER_ACCESS_LIST", []),
    userProgramAccessList: _.get(window, "_CS_USER_PROGRAM_ACCESS_LIST", []),
    userProgramDealerAccessList: _.get(window, "_CS_USER_PROGRAM_DEALER_ACCESS_LIST", []),
    selectedDealer: null,
    selectedProgram: null,
    programPermissions: _.get(window, "_CS_PROGRAM_PERMISSIONS", {}),
    featureFlags: _.get(window, "_CS_FEATURE_FLAGS", null),
    nesnaFeatureSubscriptionEnabled: _.get(window, "_CS_NESNA_FEATURE_SUBSCRIPTION_ENABLED", false),
    isAtlasDealerTrackPhase2Enabled: _.get(window, "_CS_ATLAS_DEALER_TRACK_PHASE_2_ENABLED", false),
    penFeatureSubscriptionEnabled: _.get(window, "_CS_PEN_FEATURE_SUBSCRIPTION_ENABLED", false),
};

const mutations = {
    ...make.mutations(initialState),
};

const getters = {
    name: (state) => {
        return `${state.firstName} ${state.lastName}`;
    },
    isDealerEnrolledInBoost: (state) => {
        if (state.selectedDealer && state.selectedDealer.id) {
            return state.selectedDealer.isBoostEnrolled;
        }

        return null;
    },
    isProgramUserEnrolledInNissanBuyAtHome: (state) => {
        const data = state.userProgramAccessList;
        const pattern = /^\s*nissan\s+buy@home\s*$/i;
        if (!data) return false;
        const filtered = data.filter((item) => {
            return pattern.test(item?.name || "");
        });
        return !_.isNil(filtered) && !_.isEmpty(filtered);
    },
    hasDealerPermission: (state) => (dealerId, permission) => {
        if (_.isNil(dealerId) || _.isNil(permission)) {
            return false;
        }

        const dealerPermissions = _.get(state.dealerPermissions, dealerId);
        if (_.isNil(dealerPermissions) || _.size(dealerPermissions) === 0) {
            return false;
        }

        return _.indexOf(dealerPermissions, permission) !== -1;
    },
    hasAnyDealerPermission: (state) => (dealerId) => {
        if (_.isNil(dealerId)) {
            return false;
        }

        const dealerPermissions = _.get(state, "dealerPermissions");
        if (_.isNil(dealerPermissions)) {
            return false;
        }

        return _.has(dealerPermissions, dealerId);
    },
    hasAuthority: (state) => (authorities) => {
        if (!_.isArray(authorities)) {
            return false;
        }

        const auths = _.map(state.authorizations, (auth) => _.get(auth, "authority", ""));
        return _.intersection(authorities, auths).length === authorities.length;
    },
    hasAnyAuthorities: (state) => (authorities) => {
        if (!_.isArray(authorities)) {
            return false;
        }

        const auths = _.map(state.authorizations, (auth) => _.get(auth, "authority", ""));
        return _.intersection(authorities, auths).length >= 1;
    },
    isEnrolledInWarrantyProgram: (state) => (dealerId) => {
        if (_.isNil(dealerId)) {
            return false;
        }

        const userDealerAccessList = _.get(state, "userDealerAccessList");
        if (_.isNil(userDealerAccessList)) {
            return false;
        }

        const selectedDealer = _.find(userDealerAccessList, ["id", dealerId]);
        if (_.isNil(selectedDealer)) {
            return false;
        }

        return selectedDealer.enrolledInWarrantyProgram;
    },
    isNnaDealer: (state) => (dealerId) => {
        let dealerNnaFlag = true;
        const userDealerAccessList = _.get(state, "userDealerAccessList");

        if (!_.isArray(dealerId)) {
            const selectedDealer = _.find(userDealerAccessList, ["id", dealerId]);

            if (selectedDealer === undefined || selectedDealer.nnaDealerId === null) {
                dealerNnaFlag = false;
            }
        } else {
            _.forEach(dealerId, function (value) {
                const selectedDealer = _.find(userDealerAccessList, ["id", value]);

                if (!selectedDealer || selectedDealer.nnaDealerId === null) {
                    dealerNnaFlag = false;
                }
            });
        }
        return dealerNnaFlag;
    },
    isProgramTypeUser: (state, getters) => {
        return getters.hasAuthority(["ROLE_PROGRAM"]);
    },
    hasProgramPermission: (state) => (userId, permission) => {
        if (_.isNil(userId) || _.isNil(permission)) {
            return false;
        }

        const programPermissions = _.get(state.programPermissions, userId);
        if (_.isNil(programPermissions) || _.size(programPermissions) === 0) {
            return false;
        }

        return _.indexOf(programPermissions, permission) !== -1;
    },
    isBMWProgramUser: (state) => (programId) => {
        return ProgramEnum.BMW.indexOf(programId) !== -1;
    },
};

const actions = {
    ...make.actions(initialState),
};

export default {
    namespaced: true,
    state: initialState,
    getters,
    mutations,
    actions,
};
